// Loaded before the main bundle and dormant until something calls it. It imports
// nothing from the app: at click time it reads the bridge, and without one falls
// back to a page that loads no app code.

import { wipeStorage } from "@patchweave/storage-wipe";
import type {
  RecoveryBridge,
  RecoveryReason,
  RecoveryShellOperations,
  RecoverySyncOutcome,
} from "./types";

interface ShellState {
  visible: boolean;
  reason: RecoveryReason | null;
  loading: boolean;
  message: string | null;
  error: string | null;
  confirming: boolean;
  confirmAction: "wipe" | "discard" | null;
  pendingOps: PendingOps;
}

type PendingOps =
  | { kind: "missing" }
  | { kind: "uncounted" }
  | { kind: "counted"; changes: number; photos: number };

const styles = {
  overlay: [
    "position:fixed",
    "inset:0",
    "background:rgba(15,23,42,0.92)",
    "color:#f8fafc",
    "font:14px/1.5 system-ui,-apple-system,sans-serif",
    "z-index:2147483647",
    "display:flex",
    "align-items:center",
    "justify-content:center",
    "padding:24px",
  ].join(";"),
  panel: [
    "background:#0f172a",
    "border:1px solid #334155",
    "border-radius:8px",
    "max-width:480px",
    "width:100%",
    "padding:24px",
    "box-shadow:0 10px 40px rgba(0,0,0,0.5)",
  ].join(";"),
  title: "font-size:18px;font-weight:600;margin:0 0 8px",
  subtitle: "font-size:13px;color:#94a3b8;margin:0 0 20px",
  buttonRow: "display:flex;flex-direction:column;gap:8px;margin-bottom:12px",
  button: [
    "background:#1e293b",
    "color:#f8fafc",
    "border:1px solid #475569",
    "border-radius:6px",
    "padding:12px 16px",
    "font:inherit",
    "cursor:pointer",
    "text-align:left",
  ].join(";"),
  buttonDanger: "border-color:#dc2626;color:#fca5a5",
  buttonDisabled: "opacity:0.5;cursor:wait",
  message: "font-size:13px;color:#cbd5e1;margin:8px 0 0;min-height:1.5em",
  error: "font-size:13px;color:#fca5a5;margin:8px 0 0",
  footer: "display:flex;justify-content:flex-end;margin-top:16px",
  closeButton: [
    "background:transparent",
    "color:#94a3b8",
    "border:none",
    "font:inherit",
    "cursor:pointer",
  ].join(";"),
  confirmRow: [
    "border:1px solid #7f1d1d",
    "background:rgba(127,29,29,0.15)",
    "border-radius:6px",
    "padding:12px",
    "margin-top:4px",
    "display:none",
  ].join(";"),
  confirmRowVisible: "display:block",
  confirmText: "font-size:13px;color:#fecaca;margin:0 0 12px",
  confirmButtons: "display:flex;flex-direction:column;gap:8px",
  pending: "font-size:13px;color:#94a3b8;margin:0 0 12px",
};

function getBridge(): RecoveryBridge | null {
  return window.__recoveryBridge ?? null;
}

const RECOVERY_PAGE = "/recovery.html";

function onRecoveryPage(): boolean {
  return window.location.pathname === RECOVERY_PAGE;
}

// Only safe on {@link RECOVERY_PAGE} — anywhere else the app's worker holds
// the database open and the delete fails.
async function eraseHere(): Promise<void> {
  try {
    await wipeStorage({
      preserveUserSession: false,
      onStepFailed: (step, error) => console.warn("[Recovery]", step, error),
    });
  } catch {
    throw new Error(
      "Everything else was erased, but the database is still open in another " +
        "tab. Close every other tab for this site, then erase again.",
    );
  }
}

const state: ShellState = {
  visible: false,
  reason: null,
  loading: false,
  message: null,
  error: null,
  confirming: false,
  confirmAction: null,
  pendingOps: { kind: "missing" },
};

const WIPE_LABEL = "Sign out and erase local data";
const WIPE_CONFIRM_LABEL = "Yes, sign out and erase";
const SYNC_LABEL = "Sync saved data";
const IGNORE_LABEL = "Ignore and continue";
const EXPORT_LABEL = "Export raw events";
const DISCARD_LABEL = "Discard and continue";
const DISCARD_CONFIRM_LABEL = "Yes, discard and continue";

let overlayEl: HTMLDivElement | null = null;
let messageEl: HTMLParagraphElement | null = null;
let errorEl: HTMLParagraphElement | null = null;
let pendingEl: HTMLParagraphElement | null = null;
let subtitleEl: HTMLParagraphElement | null = null;
let actionRowEl: HTMLDivElement | null = null;
let confirmButtons: HTMLButtonElement[] = [];
let buttons: HTMLButtonElement[] = [];

function render(): void {
  if (!state.visible) {
    if (overlayEl?.parentElement) overlayEl.parentElement.removeChild(overlayEl);
    overlayEl = null;
    messageEl = null;
    errorEl = null;
    pendingEl = null;
    subtitleEl = null;
    actionRowEl = null;
    confirmButtons = [];
    buttons = [];
    return;
  }

  if (!overlayEl) {
    overlayEl = document.createElement("div");
    overlayEl.setAttribute("data-recovery-shell", "");
    overlayEl.setAttribute("style", styles.overlay);

    const panel = document.createElement("div");
    panel.setAttribute("style", styles.panel);

    const title = document.createElement("h2");
    title.setAttribute("style", styles.title);
    title.textContent = "Recovery";
    panel.appendChild(title);

    subtitleEl = document.createElement("p");
    subtitleEl.setAttribute("style", styles.subtitle);
    panel.appendChild(subtitleEl);

    pendingEl = document.createElement("p");
    pendingEl.setAttribute("style", styles.pending);
    pendingEl.setAttribute("data-recovery-pending", "");
    panel.appendChild(pendingEl);

    actionRowEl = document.createElement("div");
    actionRowEl.setAttribute("style", styles.buttonRow);
    panel.appendChild(actionRowEl);

    const confirmRow = document.createElement("div");
    confirmRow.setAttribute("data-recovery-confirm-row", "");
    confirmRow.setAttribute("style", styles.confirmRow);
    const confirmText = document.createElement("p");
    confirmText.setAttribute("style", styles.confirmText);
    confirmText.textContent =
      "This erases all data stored in this browser — including any " +
      "unsynced changes. You'll be signed out and redirected.";
    confirmRow.appendChild(confirmText);
    const confirmButtonsEl = document.createElement("div");
    confirmButtonsEl.setAttribute("style", styles.confirmButtons);
    const cancelBtn = createButton("Cancel", () => cancelConfirm());
    const confirmBtn = createButton(
      WIPE_CONFIRM_LABEL,
      () => runAction(state.confirmAction === "discard" ? "discard" : "wipe"),
      true,
    );
    confirmButtonsEl.appendChild(cancelBtn);
    confirmButtonsEl.appendChild(confirmBtn);
    confirmButtons = [cancelBtn, confirmBtn];
    confirmRow.appendChild(confirmButtonsEl);
    panel.appendChild(confirmRow);

    messageEl = document.createElement("p");
    messageEl.setAttribute("style", styles.message);
    messageEl.setAttribute("data-recovery-message", "");
    panel.appendChild(messageEl);

    errorEl = document.createElement("p");
    errorEl.setAttribute("style", styles.error);
    errorEl.setAttribute("data-recovery-error", "");
    panel.appendChild(errorEl);

    const footer = document.createElement("div");
    footer.setAttribute("style", styles.footer);
    const close = document.createElement("button");
    close.setAttribute("style", styles.closeButton);
    close.textContent = "Close";
    close.onclick = () => api.close();
    footer.appendChild(close);
    panel.appendChild(footer);

    overlayEl.appendChild(panel);
    document.body.appendChild(overlayEl);
  }

  if (subtitleEl) subtitleEl.textContent = describeReason(state.reason);
  if (messageEl) messageEl.textContent = state.message ?? "";
  if (errorEl) errorEl.textContent = state.error ?? "";
  if (pendingEl) pendingEl.textContent = describePending(state.pendingOps);
  rebuildActionButtons();
  for (const b of [...buttons, ...confirmButtons]) {
    b.disabled = state.loading;
    const base = b.dataset["danger"] === "1"
      ? `${styles.button};${styles.buttonDanger}`
      : styles.button;
    b.setAttribute("style", state.loading ? `${base};${styles.buttonDisabled}` : base);
  }
  const confirmTextEl = overlayEl?.querySelector<HTMLParagraphElement>(
    "[data-recovery-confirm-row] p",
  );
  if (confirmTextEl) {
    confirmTextEl.textContent =
      state.confirmAction === "discard"
        ? "This rebuilds local data from the server and removes the unsynced changes from the previous version. Export them first if you want a copy."
        : "This erases all data stored in this browser — including any unsynced changes. You'll be signed out and redirected.";
  }
  const confirmActionBtn = confirmButtons[1];
  if (confirmActionBtn) {
    confirmActionBtn.textContent =
      state.confirmAction === "discard"
        ? DISCARD_CONFIRM_LABEL
        : WIPE_CONFIRM_LABEL;
  }
  const confirmRowEl = overlayEl?.querySelector<HTMLDivElement>(
    "[data-recovery-confirm-row]",
  );
  if (confirmRowEl) {
    confirmRowEl.setAttribute(
      "style",
      state.confirming
        ? `${styles.confirmRow};${styles.confirmRowVisible}`
        : styles.confirmRow,
    );
  }
}

function rebuildActionButtons(): void {
  if (!actionRowEl) return;
  actionRowEl.replaceChildren();
  buttons = [];
  if (typeof window.__recoveryResetRoot === "function") {
    buttons.push(createButton(IGNORE_LABEL, () => runIgnore()));
  }
  if (state.reason === "schema-stale" || state.reason === "server-reset") {
    buttons.push(createButton(EXPORT_LABEL, () => runAction("export")));
    buttons.push(createButton(DISCARD_LABEL, () => requestDiscard(), true));
  } else if (getBridge()) {
    // Without a bridge, Sync can only report that it is unavailable.
    buttons.push(createButton(SYNC_LABEL, () => runAction("sync")));
  }
  buttons.push(createButton(WIPE_LABEL, () => requestWipe(), true));
  for (const b of buttons) actionRowEl.appendChild(b);
}

function requestWipe(): void {
  state.confirmAction = "wipe";
  state.confirming = true;
  state.error = null;
  state.message = null;
  render();
}

function requestDiscard(): void {
  state.confirmAction = "discard";
  state.confirming = true;
  state.error = null;
  state.message = null;
  render();
}

function runIgnore(): void {
  try {
    window.__recoveryResetRoot?.();
  } catch {
    // Reset shouldn't throw — but if it does, hiding the shell still lets
    // the user try Sync or Wipe. Don't surface to the user; the boundary
    // will re-pop the shell if the error recurs.
  }
  api.close();
}

// Cancels whichever confirm flow is open — wipe or discard.
function cancelConfirm(): void {
  state.confirming = false;
  state.confirmAction = null;
  render();
}

function counted(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

function describePending(pendingOps: PendingOps): string {
  switch (pendingOps.kind) {
    case "missing":
      return "";
    case "uncounted":
      return "This device may have changes waiting to sync, and they could not be counted.";
    case "counted": {
      const parts = [
        pendingOps.changes > 0 ? counted(pendingOps.changes, "change") : null,
        pendingOps.photos > 0 ? counted(pendingOps.photos, "photo") : null,
      ].filter((part) => part !== null);
      if (parts.length === 0) return "All changes are synced.";
      return `${parts.join(" and ")} waiting to sync.`;
    }
  }
}

async function refreshPendingCounts(): Promise<void> {
  const bridge = getBridge();
  if (!bridge) {
    state.pendingOps = { kind: "missing" };
    render();
    return;
  }
  try {
    const { scheduledOrPendingEventCount, pendingPhotoCount } = await bridge.getPendingCounts();
    state.pendingOps =
      scheduledOrPendingEventCount === null || pendingPhotoCount === null
        ? { kind: "uncounted" }
        : { kind: "counted", changes: scheduledOrPendingEventCount, photos: pendingPhotoCount };
  } catch {
    state.pendingOps = { kind: "uncounted" };
  }
  render();
}

function createButton(
  label: string,
  onClick: () => void,
  danger = false,
): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.textContent = label;
  b.setAttribute(
    "style",
    danger ? `${styles.button};${styles.buttonDanger}` : styles.button,
  );
  if (danger) b.dataset["danger"] = "1";
  b.onclick = onClick;
  return b;
}

function describeReason(reason: RecoveryReason | null): string {
  switch (reason) {
    case "main-crash":
      return "The app crashed. Use the actions below to recover.";
    case "bundle-missing":
      return "The main bundle did not load. Sync is unavailable; sign out and erase local data to start fresh.";
    case "schema-stale":
      return "You have unsynced changes from a previous version of the app that can no longer be opened. Export them if you want a copy, then discard to rebuild and continue.";
    case "server-reset":
      return "The server's data was reset, so your unsynced changes can no longer be sent. Export them if you want a copy, then discard to re-sync and continue.";
    case "wipe-failed":
      return "Couldn't finish erasing local data — the local database is still in use. Reload the page, then sign out and erase again.";
    case "user-request":
    case null:
    default:
      return "Direct access to sync and sign-out — independent of the main app.";
  }
}

async function runAction(kind: "sync" | "wipe" | "export" | "discard"): Promise<void> {
  state.loading = true;
  state.error = null;
  state.message = pendingMessage(kind);
  render();

  try {
    const bridge = getBridge();
    if (kind === "sync") {
      if (!bridge) {
        throw new Error("Main app not loaded — sync is unavailable in degraded mode.");
      }
      const outcome = await bridge.sync();
      applySyncOutcome(outcome);
    } else if (kind === "export") {
      if (!bridge?.exportEvents) {
        throw new Error("Export is unavailable — the recovery bridge is not loaded.");
      }
      await bridge.exportEvents();
      state.message = "Exported recovery-events.json.";
    } else if (kind === "discard") {
      if (!bridge?.discardAndContinue) {
        throw new Error("Discard is unavailable — the recovery bridge is not loaded.");
      }
      await bridge.discardAndContinue();
      return;
    } else {
      if (bridge) {
        await bridge.wipeAndLogout();
      } else if (onRecoveryPage()) {
        await eraseHere();
        window.location.href = "/";
      } else {
        // This page's worker holds the file-system handles with no bridge to release
        // them, and the recovery page loads no app code, so the wipe can run there.
        window.location.href = RECOVERY_PAGE;
      }
      return;
    }
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
    state.message = null;
  } finally {
    state.loading = false;
    state.confirming = false;
    state.confirmAction = null;
    render();
    if (kind === "sync") {
      // Fire-and-forget — render is the source of truth; pending refresh
      // will trigger its own render when the count comes back.
      void refreshPendingCounts();
    }
  }
}

function applySyncOutcome(outcome: RecoverySyncOutcome): void {
  if (outcome.ok) {
    state.message = "Sync complete.";
    state.error = null;
    return;
  }
  state.message = null;
  switch (outcome.reason) {
    case "offline":
      state.error =
        "You appear to be offline. Your changes are queued locally and will sync when you reconnect.";
      return;
    case "auth":
      state.error =
        "You're not signed in. Sign out and erase, then sign in again.";
      return;
    case "error":
      state.error = `Sync failed: ${outcome.message}`;
      return;
  }
}

function pendingMessage(kind: "sync" | "wipe" | "export" | "discard"): string {
  switch (kind) {
    case "sync":
      return "Syncing…";
    case "wipe":
      return "Erasing local data and signing out…";
    case "export":
      return "Exporting…";
    case "discard":
      return "Rebuilding local data…";
  }
}

const api: RecoveryShellOperations = {
  open(reason) {
    state.visible = true;
    state.reason = reason;
    state.error = null;
    state.message = null;
    state.confirming = false;
    state.confirmAction = null;
    state.pendingOps = { kind: "missing" };
    render();
    void refreshPendingCounts();
  },
  close() {
    state.visible = false;
    state.confirming = false;
    state.confirmAction = null;
    render();
  },
  isVisible() {
    return state.visible;
  },
};

if (typeof window !== "undefined") {
  window.__recovery = api;
  bindShortcut();
  // Nothing else runs on the recovery page, so it opens itself.
  if (onRecoveryPage()) api.open("bundle-missing");
}

// Opens the shell from anywhere, and lives in the recovery bundle so it works
// when feature code is broken. Idempotent, so a hot reload stacks no duplicate
// listeners.
function bindShortcut(): void {
  const flag = "__recoveryShortcutBound" as const;
  const w = window as Window & { [k in typeof flag]?: boolean };
  if (w[flag]) return;
  w[flag] = true;
  window.addEventListener("keydown", (event) => {
    if (!event.altKey) return;
    if (!(event.ctrlKey || event.metaKey)) return;
    if (event.key !== "r" && event.key !== "R") return;
    event.preventDefault();
    api.open("user-request");
  });
}

export {};
